import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import type {
  IListSelectorGroupConfig,
  IListSelectorSearchConfig,
  IListSelectorSortConfig,
  SelectionMode,
} from '~/client/src/shared/components/ListSelector/types'
import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import { compareGroupLabelsUnassignedLast } from '~/client/src/shared/utils/compareGroupsByLabel'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import {
  ICompanyMetadata,
  ICurrentCompanyInfo,
} from './useCompanyListSelectorData'

export enum CompanyGroupingOption {
  NONE = 'None',
  TYPE = 'Type',
  TRADE = 'Trade',
}

export interface ICompanyListSelectorProps {
  selectedCompanyIds: Set<string>
  onCompanyToggle?: (companyId: string) => void
  onToggleAll?: (companyIds: string[], selectAll: boolean) => void
  companies: Company[]
  companyMetadata?: Map<string, ICompanyMetadata>
  title?: string
  showActions?: boolean
  selectionMode?: SelectionMode
  onClose?: () => void
  onApply?: (selectedCompanyIds: Set<string>) => void
  className?: string
  defaultGrouping?: CompanyGroupingOption
  currentCompany?: ICurrentCompanyInfo
  disableApplyWhenEmpty?: boolean
}

interface ICompanyItem {
  id: string
  company: Company
}

function getSortingOptions(): IOptionItem[] {
  return [{ key: 'name', label: Localization.translator.name, glyph: 'name' }]
}

function getGroupingOptions(): IOptionItem[] {
  return [
    { key: CompanyGroupingOption.TYPE, label: Localization.translator.type },
    { key: CompanyGroupingOption.TRADE, label: Localization.translator.trade },
  ]
}

function getCategoryKeys(
  companyId: string,
  option: CompanyGroupingOption,
  companyMetadata?: Map<string, ICompanyMetadata>,
): string[] {
  const meta = companyMetadata?.get(companyId)
  if (!meta) return [UNASSIGNED]

  switch (option) {
    case CompanyGroupingOption.TYPE:
      return meta.typeTagNames?.length ? meta.typeTagNames : [UNASSIGNED]
    case CompanyGroupingOption.TRADE:
      return meta.tradeNames?.length ? meta.tradeNames : [UNASSIGNED]
    default:
      return [CompanyGroupingOption.NONE]
  }
}

const CompanyListSelector: React.FC<ICompanyListSelectorProps> = ({
  selectedCompanyIds,
  onCompanyToggle,
  onToggleAll,
  companies,
  companyMetadata,
  title,
  showActions,
  selectionMode,
  onClose,
  onApply,
  className,
  defaultGrouping = CompanyGroupingOption.NONE,
  currentCompany,
  disableApplyWhenEmpty,
}) => {
  const [isOnlyMyCompany, setIsOnlyMyCompany] = useState(false)
  const toggleOnlyMyCompany = useCallback(
    () => setIsOnlyMyCompany(prev => !prev),
    [],
  )

  const items: ICompanyItem[] = useMemo(() => {
    let result = companies

    if (isOnlyMyCompany && currentCompany?.companyId) {
      result = result.filter(c => c.id === currentCompany.companyId)
    }

    return result.map(company => ({ id: company.id, company }))
  }, [companies, isOnlyMyCompany, currentCompany])

  const searchConfig = useMemo(
    (): IListSelectorSearchConfig<ICompanyItem> => ({
      searchFn: (item, query) => {
        const { company } = item
        return (
          company.name.toLowerCase().includes(query) ||
          (company.code && company.code.toLowerCase().includes(query))
        )
      },
    }),
    [],
  )

  const sortConfig = useMemo(
    (): IListSelectorSortConfig<ICompanyItem> => ({
      options: getSortingOptions(),
      defaultKey: 'name',
      sortFn: (a, b, sortOrder) => {
        return a.company.name.localeCompare(b.company.name) * sortOrder
      },
    }),
    [],
  )

  const groupingConfig = useMemo(
    (): IListSelectorGroupConfig<ICompanyItem> => ({
      options: getGroupingOptions(),
      defaultKey:
        defaultGrouping === CompanyGroupingOption.NONE ? null : defaultGrouping,
      buildGroupConfig: (groupKey: string) => {
        const option = groupKey as CompanyGroupingOption
        if (option === CompanyGroupingOption.NONE) return undefined

        return {
          groupBy: (item: ICompanyItem) =>
            getCategoryKeys(item.company.id, option, companyMetadata),
          // Label only — the band header renders the count in its own slot.
          getGroupLabel: (key: string) => key,
          sortGroups: compareGroupLabelsUnassignedLast,
        }
      },
    }),
    [defaultGrouping, companyMetadata],
  )

  const renderContent = useCallback((item: ICompanyItem) => {
    const { company } = item

    return (
      <>
        <Avatar
          src={company.avatarUrl}
          size={AvatarSize.Minuscule}
          avatarType={AvatarType.Company}
        />
        <span className="tw:truncate">{company.formattedName}</span>
      </>
    )
  }, [])

  return (
    <ListSelector.Root<ICompanyItem>
      items={items}
      selectedIds={selectedCompanyIds}
      onItemToggle={onCompanyToggle}
      onToggleAll={onToggleAll}
      draft={showActions}
      selectionMode={selectionMode}
      onClose={onClose}
      search={searchConfig}
      sort={sortConfig}
      grouping={groupingConfig}
      className={className}
    >
      {title && <ListSelector.Title title={title} onClose={onClose} />}
      <ListSelector.Toolbar
        quickFilters={
          currentCompany
            ? { isOnlyMyCompany, onToggleMyCompany: toggleOnlyMyCompany }
            : undefined
        }
      />
      <ListSelector.Counter
        formatTotal={Localization.translator.xCompaniesCount}
      />
      <ListSelector.List renderItemContent={renderContent} />
      {showActions && (
        <ListSelector.Actions
          onApply={onApply}
          onClose={onClose}
          disableApplyWhenEmpty={disableApplyWhenEmpty}
        />
      )}
    </ListSelector.Root>
  )
}

export default CompanyListSelector
